import { useEffect, useState } from 'react';
import { getYear } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { bool } from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { track } from '@fff-web/fff-analytics';
import { showErrorToast } from '@fff-web/fff-ui-shared';
import { getOrders } from '../../../services/orders';
import LoadingLogo from '../shared-components/LoadingLogo/LoadingLogo';
import PageContainer from '../shared-components/PageContainer/PageContainer';
import {
  getDropship,
  getItemsCount,
  getSaleTypes,
  getShipped,
  getTotalProducts,
} from '../utils/orders';
import OrderDetails from './OrderDetails';
import OrdersList from './OrdersList';
import * as styles from './styles.module.scss';

const filterOldOrders = (orders) =>
  orders &&
  orders.filter((order) => {
    const year = getYear(order.billingDate);
    return year >= 2022;
  });
const getTrackingData = (order) => {
  const { billingDate, shipments, orderTotalInCents } = order;
  const saleType = getSaleTypes(shipments);
  const items = getItemsCount(shipments);
  const orderTotal = orderTotalInCents / 100;
  return {
    order_type: saleType,
    total_products: getTotalProducts(shipments),
    order_date: billingDate,
    order_total: orderTotal,
    total_units: items,
  };
};

const Orders = ({ isDesktop }) => {
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const { orders: ordersFF } = useFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const orderError = location?.state?.error || null;

  const onClickOrderDetails = (order) => setOrderDetails(order);
  const onClickBack = () => setOrderDetails(null);
  const onClickAllInvoices = () => navigate('/my-account/invoice-history');
  const handleTrackOrderViewed = (order, position) => {
    const trackingData = getTrackingData(order, position);
    const { shipments } = order;
    const totalShipments = getShipped(shipments).length;
    const totalDropships = getDropship(shipments).length;
    track({
      trackName: 'Order Viewed',
      properties: {
        ...trackingData,
        total_shipments: totalShipments,
        event_position_order: position,
        tracking_available: totalShipments > 0,
        dropship: totalDropships > 0,
      },
    });
  };
  const handleTrackShipment = (order, trackingNumber) => {
    const trackingData = getTrackingData(order);
    track({
      trackName: 'Shipment Tracked',
      properties: {
        ...trackingData,
        tracking_number: trackingNumber,
      },
    });
  };
  const renderScreen = () => {
    if (loadingOrders) return <LoadingLogo />;
    if (orderDetails)
      return (
        <OrderDetails
          order={orderDetails}
          onClickBack={onClickBack}
          trackShipment={handleTrackShipment}
        />
      );
    return (
      <OrdersList
        orders={orders}
        isDesktop={isDesktop}
        onClickAllInvoices={onClickAllInvoices}
        onClickOrderDetails={onClickOrderDetails}
        trackOrderViewed={handleTrackOrderViewed}
        trackShipment={handleTrackShipment}
      />
    );
  };

  useEffect(() => {
    const fetchOrders = async () => {
      const response = await getOrders();
      const filteredOrders = ordersFF ? response : filterOldOrders(response);
      setOrders(filteredOrders);
      setLoadingOrders(false);
    };
    fetchOrders();
  }, [ordersFF]);

  useEffect(() => {
    if (orderError) {
      showErrorToast('Error', orderError);
    }
  }, [orderError]);

  return (
    <PageContainer>
      <section className={styles.orders}>{renderScreen()}</section>
    </PageContainer>
  );
};

Orders.propTypes = {
  isDesktop: bool.isRequired,
};

export default Orders;
